import type { ExposureResult, WeightBucket } from '@prm/shared';
import { num, pct, usd } from '../format';
import { Panel } from './Panel';
import { FlagList } from './FlagList';

const SECTOR_SOURCE_TITLE: Record<string, string> = {
  manual: 'Manual override set on the position and saved with the portfolio',
  map: 'Bundled ticker→sector map',
  live: 'Live provider lookup (Yahoo quoteSummary)',
  unknown: 'Unclassified — excluded from sector limits. Set a sector on the position to include it.',
};

interface Props {
  exposure: ExposureResult;
  maxSectorWeight: number;
  maxPositionWeight: number;
}

export function ExposureCard({ exposure, maxSectorWeight, maxPositionWeight }: Props) {
  const c = exposure.concentration;

  return (
    <>
      <Panel title="Exposure" sub={`${exposure.positions.length} positions · ${usd(exposure.equity)} equity`}>
        <div className="kv">
          <Stat k="Top position" v={pct(c.topWeight)} note={c.topTicker ?? '—'} over={c.topWeight > maxPositionWeight} />
          <Stat k="Top 3" v={pct(c.top3Weight)} note="of gross" over={c.top3Weight > 0.5} />
          <Stat k="HHI" v={num(c.hhi, 3)} note="concentration" />
          <Stat k="Naive N" v={num(c.effectiveNaiveN, 1)} note="pre-correlation" />
          <Stat k="Beta coverage" v={pct(exposure.betaCoverage)} note="of gross" over={exposure.betaCoverage < 0.9} />
        </div>

        <Section title="Sector weights" />
        <Bars buckets={exposure.sectors} limit={maxSectorWeight} />

        <Section title="Role" />
        <Bars buckets={exposure.roles} />

        <Section title="Conviction" />
        <Bars buckets={exposure.convictions.slice().sort((a, b) => Number(b.key) - Number(a.key))} labelPrefix="score " />
      </Panel>

      <Panel title="Exposure flags" sub={`${exposure.flags.length}`} flush>
        <FlagList flags={exposure.flags} empty="Book is inside all configured exposure limits." />
      </Panel>

      <Panel title="Positions" sub="sorted by economic exposure · betas Blume-adjusted unless structural" flush>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Sector</th>
                <th>Role</th>
                <th>Conv</th>
                <th>Shares</th>
                <th>Price</th>
                <th>Value</th>
                <th>Weight</th>
                <th title="Blume-adjusted: 0.67 × raw + 0.33, after capping raw to [−1, 3]">Beta (adj)</th>
                <th>β Contrib</th>
              </tr>
            </thead>
            <tbody>
              {exposure.positions.map((p) => (
                <tr key={p.id}>
                  <td className="ticker">
                    {p.ticker}
                    {p.error ? <span className="down" title={p.error}> ⚠</span> : null}
                    {Math.abs(p.leverageFactor) !== 1 ? (
                      <span className="chip chip--lev" title={`${p.leverageFactor}x structural leverage`}>
                        {p.leverageFactor > 0 ? '' : ''}
                        {p.leverageFactor}x
                      </span>
                    ) : null}
                  </td>
                  <td className="dim" style={{ textAlign: 'left' }}>
                    {p.sector}
                    {/* Provenance stays visible: a hand-set sector and a live
                        lookup are not the same kind of fact. */}
                    <span className={`src src--${p.sectorSource}`} title={SECTOR_SOURCE_TITLE[p.sectorSource]}>
                      {p.sectorSource === 'unknown' ? '?' : p.sectorSource}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge--role badge--${p.role}`}>{p.role}</span>
                  </td>
                  <td className="dim">{p.conviction}</td>
                  <td className="num">{p.shares === null ? '—' : num(p.shares, 0)}</td>
                  <td className="num">{p.price === null ? '—' : num(p.price)}</td>
                  <td className="num">
                    {usd(p.value)}
                    {Math.abs(p.leverageFactor) !== 1 ? (
                      <div className="faint" style={{ fontSize: 9.5 }} title="Economic notional">
                        {usd(p.exposureValue)} exp
                      </div>
                    ) : null}
                  </td>
                  <td className={`num ${Math.abs(p.exposureWeight) > maxPositionWeight ? 'down' : ''}`}>
                    {pct(p.exposureWeight)}
                  </td>
                  <td className="num dim">
                    {p.beta === null ? (
                      '—'
                    ) : (
                      <span
                        title={
                          p.betaStructural
                            ? `raw ${num(p.betaRaw)} used unadjusted — structural leverage, not estimation noise`
                            : p.betaRaw === null
                              ? undefined
                              : `raw ${num(p.betaRaw)}${p.betaCapped ? ' (capped)' : ''} → Blume-adjusted ${num(p.beta)}`
                        }
                      >
                        {num(p.beta)}
                        {p.betaStructural ? <sup className="capped capped--structural">structural</sup> : null}
                        {p.betaCapped ? <sup className="capped">capped</sup> : null}
                      </span>
                    )}
                  </td>
                  <td className="num">{num(p.betaContribution)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

function Stat({ k, v, note, over }: { k: string; v: string; note?: string; over?: boolean }) {
  return (
    <div className="kv__item">
      <div className="kv__k">{k}</div>
      <div className={`kv__v num ${over ? 'down' : ''}`}>{v}</div>
      {note ? <div className="faint" style={{ fontSize: 10 }}>{note}</div> : null}
    </div>
  );
}

function Section({ title }: { title: string }) {
  return (
    <div className="kv__k" style={{ margin: '14px 0 6px', color: 'var(--muted)' }}>
      {title}
    </div>
  );
}

function Bars({ buckets, limit, labelPrefix = '' }: { buckets: WeightBucket[]; limit?: number; labelPrefix?: string }) {
  if (buckets.length === 0) return <div className="empty">No data.</div>;

  // Scale bars to the largest bucket (or the limit line, if that's larger) so
  // small books don't render as a row of slivers.
  const scale = Math.max(...buckets.map((b) => Math.abs(b.weight)), limit ?? 0, 0.01);

  return (
    <div className="bars">
      {buckets.map((b) => {
        const w = Math.abs(b.weight);
        const over = limit !== undefined && w > limit;
        return (
          <div className="bar" key={b.key}>
            <div className="bar__label" title={`${b.key} · ${b.count} position${b.count === 1 ? '' : 's'}`}>
              {labelPrefix}
              {b.key}
            </div>
            <div className="bar__track">
              <div className={`bar__fill ${over ? 'bar__fill--over' : ''}`} style={{ width: `${(w / scale) * 100}%` }} />
              {limit !== undefined ? <div className="bar__limit" style={{ left: `${(limit / scale) * 100}%` }} /> : null}
            </div>
            <div className={`bar__value num ${over ? 'down' : ''}`}>{pct(b.weight)}</div>
          </div>
        );
      })}
    </div>
  );
}
